import React from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, InputAdornment, List, ListItemButton, ListItemText,
  Stack, Switch, TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { stargraveAdvancedTechnology } from '../../data/stargrave/advancedTechnology';

interface StargraveTechnologyModalProps {
  open: boolean;
  availableCredits: number;
  onClose: () => void;
  onAdd: (technologyId: string, addForFree: boolean) => void;
}

export const StargraveTechnologyModal: React.FC<StargraveTechnologyModalProps> = ({
  open,
  availableCredits,
  onClose,
  onAdd,
}) => {
  const [selectedId, setSelectedId] = React.useState('');
  const [filter, setFilter] = React.useState('');
  const [addForFree, setAddForFree] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const selected = stargraveAdvancedTechnology.find((item) => item.id === selectedId);
  const displayed = stargraveAdvancedTechnology
    .filter((item) => !filter.trim() || `${item.name} ${item.summary}`.toLowerCase().includes(filter.trim().toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  const reset = () => {
    setSelectedId('');
    setFilter('');
    setAddForFree(false);
    setError(null);
  };
  const handleClose = () => { reset(); onClose(); };
  const handleAdd = () => {
    if (!selected) return setError('Select an item.');
    if (!addForFree && selected.cost > availableCredits) return setError('Not enough credits.');
    onAdd(selected.id, addForFree);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Cargo</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1}>
          <Typography color="text.secondary">Available: {availableCredits}cr</Typography>
          <FormControlLabel control={<Switch checked={addForFree} onChange={(event) => setAddForFree(event.target.checked)} />} label="Add found item" />
        </Stack>
        <TextField size="small" value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Search technology"
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }} />
        <List dense sx={{ maxHeight: 300, overflowY: 'auto', border: 1, borderColor: 'divider' }}>
          {displayed.map((item) => (
            <ListItemButton key={item.id} selected={selectedId === item.id}
              disabled={!addForFree && item.cost > availableCredits}
              onClick={() => { setSelectedId(item.id); setError(null); }}>
              <ListItemText primary={item.name}
                secondary={`Buy ${item.cost}cr | Sell ${item.salePrice}cr${item.uses ? ` | ${item.uses} uses` : ''}`} />
            </ListItemButton>
          ))}
        </List>
        {selected && <Box>
          <Typography variant="h6">{selected.name}</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>{selected.summary}</Typography>
          {selected.restriction && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{selected.restriction}</Typography>}
          <Typography variant="caption" color="text.secondary">{selected.gearSlots} gear slot{selected.gearSlots === 1 ? '' : 's'} | Core book p. {selected.sourcePage}</Typography>
        </Box>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleAdd} disabled={!selected}>Add Item</Button>
      </DialogActions>
    </Dialog>
  );
};
